import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Typography,
  styled,
} from '@mui/material';
import React, {useCallback, useMemo, useState} from 'react';
import {getTime} from '../../services/dateService';
import {
  getEnergyRatio,
  getEnergyRatioFromFloat,
  getSpeedMultiplier,
  getComparisonFromBaseline,
} from '../../services/measurementService';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {useTranslation} from 'react-i18next';
import {updatePlayerSessionShift} from '../../api/playerSessionShiftAPI';
import {reprocessPlayerSession} from '../../api/playerSessionAPI';
import {Baseline, PlayerSessionShift} from '../../interfaces';
import PlayerSessionShiftItem from './PlayerSessionShiftItem';
import {getPlayerSessionDetailAsync} from '../../store/playerSessionSlice';
import {SessionTypes} from '../../enums';

const SaveShiftsWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'end',
}));

interface Props {
  playerSessionIds: number[];
  type: string;
  shifts: PlayerSessionShift[];
  inReview: boolean;
  setInReview: (inReview: boolean) => void;
  isProcessingVideo: boolean;
  baseline?: Baseline;
}

const PlayerSessionShifts: React.FC<Props> = ({
  playerSessionIds,
  type,
  shifts,
  inReview,
  setInReview,
  isProcessingVideo,
  baseline,
}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const preferredUnits = useAppSelector(
    store => store.profile?.profile?.preferredUnits,
  );

  const [pendingShiftValidUpdates, setPendingShiftValidUpdates] = useState<
    {
      id: number;
      valid: boolean;
    }[]
  >([]);
  const [updatingShifts, setUpdatingShifts] = useState<boolean>(false);

  const speedMultiplier = useMemo(
    () => getSpeedMultiplier(preferredUnits === 'metric'),
    [preferredUnits],
  );
  const speedUnitLabel = useMemo(
    () =>
      preferredUnits === 'metric'
        ? (t('general.units.kphAbbrev') as string)
        : (t('general.units.mphAbbrev') as string),
    [preferredUnits, t],
  );

  const shiftsInfo = useMemo(() => {
    let shiftNumber = 1;
    return shifts.map(shift => ({
      id: shift.id,
      drill: shift.drill,
      valid: shift.valid,
      number: shift.valid ? shiftNumber++ : undefined,
      start: shift.start,
      data: [
        {
          label: t('shifts.timeAbbrev') as string,
          value:
            shift.start && shift.end ? getTime(shift.end - shift.start) : '--',
        },
        {
          label: t('sessionStats.strides') as string,
          value: shift.strideCountTotal || '--',
        },
        {
          label: t('sessionStats.strideTime') as string,
          value:
            (shift.strintTotalTime ? getTime(shift.strintTotalTime) : null) ??
            '--',
        },
        {
          label: t('sessionStats.strideSpeedAvg') as string,
          value: shift.speedAvgV2
            ? (shift.speedAvgV2 * speedMultiplier).toFixed(1)
            : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.speedAvgV2,
            baseline: baseline?.speedAvgV2,
          }),
          unit: speedUnitLabel,
        },
        {
          label: t('sessionStats.strideSpeedMax') as string,
          value: shift.speedMaxV2
            ? (shift.speedMaxV2 * speedMultiplier).toFixed(1)
            : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.speedMaxV2,
            baseline: baseline?.speedMaxV2,
          }),
          unit: speedUnitLabel,
        },
        {
          label: t('sessionStats.explosivenessAvg') as string,
          value: shift.accelAvgV2 ? (shift.accelAvgV2 / 9.8).toFixed(2) : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.accelAvgV2,
            baseline: baseline?.accelAvgV2,
          }),
          unit: t('general.units.gAbbrev') as string,
        },
        {
          label: t('sessionStats.explosivenessMax') as string,
          value: shift.accelMaxV2 ? (shift.accelMaxV2 / 9.8).toFixed(2) : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.accelMaxV2,
            baseline: baseline?.accelMaxV2,
          }),
          unit: t('general.units.gAbbrev') as string,
        },
        {
          label: t('sessionStats.agilityAvg') as string,
          value: shift.strintFilteredStrideRateAvgV2
            ? shift.strintFilteredStrideRateAvgV2.toFixed(0)
            : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.strintFilteredStrideRateAvgV2,
            baseline: baseline?.strintFilteredStrideRateAvgV2,
          }),
          unit: t('general.units.spmAbbrev') as string,
        },
        {
          label: t('sessionStats.agilityMax') as string,
          value: shift.strintFilteredStrideRateMaxV2
            ? shift.strintFilteredStrideRateMaxV2.toFixed(0)
            : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.strintFilteredStrideRateMaxV2,
            baseline: baseline?.strintFilteredStrideRateMaxV2,
          }),
          unit: t('general.units.spmAbbrev') as string,
        },
        {
          label: t('sessionStats.balancePercent') as string,
          value: shift.balance
            ? `${Math.round(shift.balance * 100)} / ${Math.round((1 - shift.balance) * 100)}`
            : '--',
          comparison: getComparisonFromBaseline({
            stat: shift.balanceNormalized,
            baseline: baseline?.balanceNormalized,
          }),
        },
        {
          label: t('sessionStats.energyRatio') as string,
          value:
            getEnergyRatioFromFloat(
              getEnergyRatio({
                ratio: shift.energyRatio,
              }),
              true,
            ) ?? '--',
          comparison: getComparisonFromBaseline({
            stat: getEnergyRatio({
              ratio: shift.energyRatio,
            }),
            baseline: baseline?.energyRatio,
          }),
          unit: t('general.units.percentSymbol') as string,
        },
        {
          label: t('sessionStats.heartRateAvg') as string,
          value: shift.hrAvg || '--',
          comparison: getComparisonFromBaseline({
            stat: shift.hrAvg,
            baseline: baseline?.hrAvg,
          }),
          unit: t('general.units.bpmAbbrev') as string,
        },
        {
          label: t('statistics.heartRateMaxTitle') as string,
          value: shift.hrMax || '--',
          unit: t('general.units.bpmAbbrev') as string,
        },
        {
          label: t('shifts.heartRateShiftStart') as string,
          value: shift.hrStart || '--',
          unit: t('general.units.bpmAbbrev') as string,
        },
        {
          label: t('shifts.heartRateShiftEnd') as string,
          value: shift.hrEnd || '--',
          unit: t('general.units.bpmAbbrev') as string,
        },
      ],
    }));
  }, [
    baseline?.accelAvgV2,
    baseline?.accelMaxV2,
    baseline?.balanceNormalized,
    baseline?.energyRatio,
    baseline?.hrAvg,
    baseline?.speedAvgV2,
    baseline?.speedMaxV2,
    baseline?.strintFilteredStrideRateAvgV2,
    baseline?.strintFilteredStrideRateMaxV2,
    shifts,
    speedMultiplier,
    speedUnitLabel,
    t,
  ]);

  const refresh = useCallback(() => {
    playerSessionIds.map(id => {
      return dispatch(getPlayerSessionDetailAsync(id));
    });
  }, [dispatch, playerSessionIds]);

  if (!shifts.length) {
    if (type === SessionTypes.practice || type === SessionTypes.skills) {
      return (
        <Alert severity="info" variant="outlined">
          {t('shifts.noDrillRuns')}
        </Alert>
      );
    }

    return (
      <Alert severity="info" variant="outlined">
        {t('shifts.noShifts')}
      </Alert>
    );
  }

  return (
    <>
      {inReview && (
        <Alert severity="info" variant="outlined" sx={{mb: 2}}>
          <Typography variant="body1">
            {t('shifts.shiftReviewInstructions')}
          </Typography>
        </Alert>
      )}
      <TableContainer sx={{mb: 2}} component={Paper}>
        <Table
          aria-label={t('ariaLabels.tables.playerSessionShifts') as string}>
          <TableBody>
            {shiftsInfo.map(shift => (
              <PlayerSessionShiftItem
                key={shift.start}
                start={shift.start || 0}
                valid={!!shift.valid}
                number={shift.number}
                drill={shift.drill}
                data={shift.data.map(d => ({
                  label: d.label,
                  value: d.value ? d.value.toString() : '',
                  comparison: d.comparison as 'up' | 'down' | undefined,
                  unit: d.unit,
                }))}
                inReview={inReview}
                onChangeValid={valid => {
                  const existingPendingShiftValidUpdate =
                    pendingShiftValidUpdates.filter(
                      pendingShiftValidUpdate =>
                        pendingShiftValidUpdate.id === shift.id,
                    )[0];
                  // if there is already a pending update for this shift
                  if (existingPendingShiftValidUpdate) {
                    // if the new valid value is the same as the existing shift
                    if (valid === shift.valid) {
                      // remove the pending update
                      setPendingShiftValidUpdates(
                        pendingShiftValidUpdates.filter(
                          pendingShiftValidUpdate =>
                            pendingShiftValidUpdate.id !== shift.id,
                        ),
                      );
                    }
                    // otherwise update the pending update
                    else {
                      setPendingShiftValidUpdates(
                        pendingShiftValidUpdates.map(
                          pendingShiftValidUpdate => {
                            if (pendingShiftValidUpdate.id === shift.id) {
                              return {
                                ...pendingShiftValidUpdate,
                                valid,
                              };
                            }
                            return pendingShiftValidUpdate;
                          },
                        ),
                      );
                    }
                  }
                  // otherwise add a new pending update
                  else {
                    setPendingShiftValidUpdates([
                      ...pendingShiftValidUpdates,
                      {
                        id: shift.id || 0,
                        valid,
                      },
                    ]);
                  }
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {inReview && (
        <>
          {isProcessingVideo && (
            <Alert severity="info" variant="outlined" sx={{mb: 2}}>
              {t('shifts.playerSessionReprocessingError')}
            </Alert>
          )}
          <SaveShiftsWrapper>
            <Button
              variant="contained"
              onClick={async () => {
                setUpdatingShifts(true);
                const shiftUpdatePromises = pendingShiftValidUpdates.map(
                  pendingShiftValidUpdate => {
                    return updatePlayerSessionShift({
                      id: pendingShiftValidUpdate.id,
                      valid: pendingShiftValidUpdate.valid,
                    });
                  },
                );
                await Promise.all(shiftUpdatePromises);
                await Promise.all(
                  playerSessionIds.map(
                    async id => await reprocessPlayerSession({id}),
                  ),
                );
                refresh();
                setUpdatingShifts(false);
                setInReview(false);
              }}
              disabled={
                pendingShiftValidUpdates.length === 0 ||
                updatingShifts ||
                isProcessingVideo
              }>
              {t('general.save')}
              {updatingShifts && (
                <CircularProgress sx={{color: '#fff', ml: 1}} size={16} />
              )}
            </Button>
          </SaveShiftsWrapper>
        </>
      )}
    </>
  );
};

export default PlayerSessionShifts;
